import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { RefreshCw, Plus, X, AlertTriangle, Tag } from 'lucide-react'
import { useAuth } from '../../../../context/AuthContext'
import AdEditor from './AdEditor'
import BulkTagsModal from '../shared/BulkTagsModal'
import '../SponsorManager.css'

function adField(a, ...keys) {
  for (const k of keys) {
    if (a?.[k] != null) return a[k]
  }
  return ''
}

// AdSpeed's full lifecycle: active → pending → archived → deleted.
const STATUS_TABS = [
  { value: 'active',   label: 'Active' },
  { value: 'pending',  label: 'Pending' },
  { value: 'archived', label: 'Archived' },
  { value: 'deleted',  label: 'Deleted' },
]

const BULK_STATUS_OPTIONS = [
  { value: '',         label: 'Set status to…' },
  { value: 'active',   label: '→ Active' },
  { value: 'pending',  label: '→ Pending' },
  { value: 'archived', label: '→ Archived' },
  { value: 'deleted',  label: '→ Deleted' },
]

export default function AdsList() {
  const { api } = useAuth()
  const navigate = useNavigate()
  const [ads, setAds] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [stale, setStale] = useState(false)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('active')
  const [status, setStatus] = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const [selected, setSelected] = useState(new Set())
  const [bulkRunning, setBulkRunning] = useState(false)
  const [showBulkTags, setShowBulkTags] = useState(false)

  // Filters
  const [tagFilter, setTagFilter] = useState('')         // '' = no tag filter
  const [typeFilter, setTypeFilter] = useState('')       // '', 'video', 'banner'
  const [zoneFilter, setZoneFilter] = useState('')       // '' = no zone filter; else zone id
  const [allTbtvTags, setAllTbtvTags] = useState([])
  const [allZones, setAllZones] = useState([])
  const [allGroups, setAllGroups] = useState([])
  const [allAdvertisers, setAllAdvertisers] = useState([])
  const [tagFilteredIds, setTagFilteredIds] = useState(null)
  const [zoneFilteredIds, setZoneFilteredIds] = useState(null)

  // Sorting
  const [sortBy, setSortBy] = useState('id')
  const [sortDir, setSortDir] = useState('asc')

  function handleSort(col) {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortBy(col); setSortDir('asc') }
  }

  const loadAds = useCallback(async () => {
    try {
      const qs = statusFilter ? `?status=${statusFilter}` : ''
      const data = await api(`/sponsors/ads${qs}`)
      setAds(data.ads || [])
      setTotal(data.total || (data.ads?.length ?? 0))
      setStale(!!data.stale)
      if (data.ok === false) {
        const msg = data.error?.message || 'AdSpeed rejected the filter'
        const isDeleted = statusFilter === 'deleted'
        setStatus(isDeleted
          ? `AdSpeed's getList API doesn't list deleted ads. The dashboard shows counts but not the items.`
          : `Error: ${msg}`)
      } else {
        setStatus('')
      }
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }, [api, statusFilter])

  useEffect(() => {
    setLoading(true)
    loadAds().finally(() => setLoading(false))
    setSelected(new Set())
  }, [loadAds])

  // Load all tbtv tags + all zones + all groups + all advertisers (for the dropdowns).
  useEffect(() => {
    // Scope the tag filter to tags actually applied to sponsor entities
    // visible from this list (ads / zones / groups). Filtering by an
    // advertiser-only or content-only tag would match nothing here.
    api('/sponsors/tags/in-use?entities=ads,zones,groups').then(d => setAllTbtvTags(d.tags || [])).catch(() => {})
    api('/sponsors/zones?status=active').then(d => setAllZones(d.zones || [])).catch(() => {})
    api('/sponsors/groups?status=active').then(d => setAllGroups(d.groups || [])).catch(() => {})
    api('/sponsors/advertisers?status=active').then(d => setAllAdvertisers(d.advertisers || [])).catch(() => {})
  }, [api])

  // Tag filter — call /by-tag and intersect.
  useEffect(() => {
    if (!tagFilter) {
      setTagFilteredIds(null)
      return
    }
    api(`/sponsors/ads/by-tag?tag=${encodeURIComponent(tagFilter)}`)
      .then(d => {
        const ids = new Set((d.ads || []).map(a => String(a.id ?? a.AdId)))
        setTagFilteredIds(ids)
      })
      .catch(e => setStatus(`Tag filter error: ${e.message}`))
  }, [api, tagFilter])

  // Zone filter — call zone detail and grab its linked ads.
  useEffect(() => {
    if (!zoneFilter) {
      setZoneFilteredIds(null)
      return
    }
    api(`/sponsors/zones/${zoneFilter}`)
      .then(d => {
        const linked = d.zone?.Ads?.Ad
        const arr = Array.isArray(linked) ? linked : linked ? [linked] : []
        const ids = new Set(arr.map(a => String(a.id ?? a.AdId)))
        setZoneFilteredIds(ids)
      })
      .catch(e => setStatus(`Zone filter error: ${e.message}`))
  }, [api, zoneFilter])

  function toggleSelect(id) {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleSelectAll() {
    const allIds = sorted.map(a => String(adField(a, 'id', 'AdId')))
    if (allIds.every(id => selected.has(id))) setSelected(new Set())
    else setSelected(new Set(allIds))
  }

  async function handleBulkStatus(newStatus) {
    if (!newStatus) return
    const ids = Array.from(selected)
    if (!confirm(
      `Set status to "${newStatus}" for ${ids.length} ad(s)?\n\n` +
      `AdSpeed allows 50 AS.Ad.edit calls per day. This will use ${ids.length}.`
    )) return

    setBulkRunning(true)
    let ok = 0, fail = 0, lastErr = ''
    for (const id of ids) {
      try {
        const res = await api(`/sponsors/ads/${id}`, {
          method: 'PATCH',
          body: JSON.stringify({ status: newStatus }),
        })
        if (res?.ok === false) throw new Error(res.error?.message || 'AdSpeed rejected')
        ok++
      } catch (e) {
        fail++
        lastErr = e.message
      }
    }
    setBulkRunning(false)
    setSelected(new Set())
    setStatus(fail === 0
      ? `Status set to "${newStatus}" for ${ok} ad(s).`
      : `Updated ${ok}, ${fail} failed. Last error: ${lastErr}`)
    await loadAds()
  }

  // Bulk-link selected ads to a single zone. Loops through selection,
  // calls POST /sponsors/ads/:id/link per ad.
  async function handleBulkLinkZone(zoneId) {
    if (!zoneId) return
    const zone = allZones.find(z => String(z.id ?? z.ZoneId ?? z.zone_id) === String(zoneId))
    if (!zone) {
      setStatus('Zone not found in cached list — refresh and try again.')
      return
    }
    const targetId = zone.id ?? zone.ZoneId ?? zone.zone_id
    const targetName = zone.name ?? zone.Name ?? ''
    const ids = Array.from(selected)
    if (!confirm(
      `Link ${ids.length} ad(s) to zone "${targetName}"?\n\n` +
      `Uses ${ids.length} AS.Ad.linkToZone calls (limit 50/day).`
    )) return

    setBulkRunning(true)
    let ok = 0, fail = 0, lastErr = ''
    for (const id of ids) {
      try {
        const res = await api(`/sponsors/ads/${id}/link`, {
          method: 'POST',
          body: JSON.stringify({ kind: 'zone', target_id: targetId, target_name: targetName }),
        })
        if (res?.ok === false) throw new Error(res.error?.message || 'AdSpeed rejected')
        ok++
      } catch (e) {
        fail++
        lastErr = e.message
      }
    }
    setBulkRunning(false)
    setSelected(new Set())
    setStatus(fail === 0
      ? `Linked ${ok} ad(s) to "${targetName}".`
      : `Linked ${ok}, ${fail} failed. Last error: ${lastErr}`)
    await loadAds()
  }

  async function handleBulkLinkGroup(groupId) {
    if (!groupId) return
    const group = allGroups.find(g => String(g.id ?? g.GroupId) === String(groupId))
    if (!group) {
      setStatus('Group not found in cached list — refresh and try again.')
      return
    }
    const targetId = group.id ?? group.GroupId
    const targetName = group.name ?? group.Name ?? ''
    const ids = Array.from(selected)
    if (!confirm(
      `Link ${ids.length} ad(s) to group "${targetName}"?\n\n` +
      `Uses ${ids.length} AS.Ad.linkToGroup calls (limit 50/day).`
    )) return

    setBulkRunning(true)
    let ok = 0, fail = 0, lastErr = ''
    for (const id of ids) {
      try {
        const res = await api(`/sponsors/ads/${id}/link`, {
          method: 'POST',
          body: JSON.stringify({ kind: 'group', target_id: targetId, target_name: targetName }),
        })
        if (res?.ok === false) throw new Error(res.error?.message || 'AdSpeed rejected')
        ok++
      } catch (e) {
        fail++
        lastErr = e.message
      }
    }
    setBulkRunning(false)
    setSelected(new Set())
    setStatus(fail === 0
      ? `Linked ${ok} ad(s) to "${targetName}".`
      : `Linked ${ok}, ${fail} failed. Last error: ${lastErr}`)
    await loadAds()
  }

  // Advertiser link uses USERNAME as the second half of the token (not
  // name/fullname). AdSpeed's link recipe: md5([Ad ID][Ad Name][Adv ID][Username]).
  async function handleBulkLinkAdvertiser(advertiserId) {
    if (!advertiserId) return
    const adv = allAdvertisers.find(a => String(a.id ?? a.AdvertiserId) === String(advertiserId))
    if (!adv) {
      setStatus('Advertiser not found in cached list — refresh and try again.')
      return
    }
    const targetId = adv.id ?? adv.AdvertiserId
    const targetName = adv.username ?? adv.Username ?? '' // MUST be username, not fullname
    const company = adv.cached_company || ''
    const displayName = company
      ? (targetName ? `${company} (${targetName})` : company)
      : (targetName || `Advertiser ${targetId}`)
    const ids = Array.from(selected)
    if (!confirm(
      `Link ${ids.length} ad(s) to advertiser "${displayName}"?\n\n` +
      `Uses ${ids.length} AS.Ad.linkToAdvertiser calls (limit 50/day).`
    )) return

    setBulkRunning(true)
    let ok = 0, fail = 0, lastErr = ''
    for (const id of ids) {
      try {
        const res = await api(`/sponsors/ads/${id}/link`, {
          method: 'POST',
          body: JSON.stringify({ kind: 'advertiser', target_id: targetId, target_name: targetName }),
        })
        if (res?.ok === false) throw new Error(res.error?.message || 'AdSpeed rejected')
        ok++
      } catch (e) {
        fail++
        lastErr = e.message
      }
    }
    setBulkRunning(false)
    setSelected(new Set())
    setStatus(fail === 0
      ? `Linked ${ok} ad(s) to "${targetName}".`
      : `Linked ${ok}, ${fail} failed. Last error: ${lastErr}`)
    await loadAds()
  }

  async function handleBulkTags(tags, mode) {
    const ids = Array.from(selected)
    setBulkRunning(true)
    let ok = 0, fail = 0
    for (const id of ids) {
      try {
        let finalTags = tags
        if (mode === 'add') {
          const existing = await api(`/sponsors/ads/${id}/tbtv-tags`)
          const existingNames = (existing?.tags || []).map(t => t.name)
          finalTags = Array.from(new Set([...existingNames, ...tags]))
        }
        await api(`/sponsors/ads/${id}/tbtv-tags`, {
          method: 'PUT',
          body: JSON.stringify({ tags: finalTags }),
        })
        ok++
      } catch {
        fail++
      }
    }
    setBulkRunning(false)
    setShowBulkTags(false)
    setSelected(new Set())
    setStatus(fail === 0
      ? `Tagged ${ok} ad(s).`
      : `Tagged ${ok}, ${fail} failed.`)
  }

  async function handleRefresh() {
    setRefreshing(true)
    try {
      await api('/sponsors/cache/bust?prefix=ads:', { method: 'POST' })
      await loadAds()
      setStatus('Refreshed from AdSpeed')
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
    setRefreshing(false)
  }

  async function handleCreate(body) {
    const res = await api('/sponsors/ads', { method: 'POST', body: JSON.stringify(body) })
    if (res?.ok === false) {
      throw new Error(res.error?.message || 'AdSpeed rejected the request')
    }
    setShowCreate(false)
    setStatus('Ad created')
    await loadAds()
  }

  // Combine: text search, tag filter (intersection with by-tag IDs), type
  // filter (heuristic on dimensions).
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return ads.filter(a => {
      const id = String(adField(a, 'id', 'AdId') || '')
      const name = String(adField(a, 'name', 'Name') || '').toLowerCase()
      if (q && !name.includes(q) && !id.toLowerCase().includes(q)) return false
      // Tag filter — intersect with by-tag result IDs
      if (tagFilteredIds && !tagFilteredIds.has(id)) return false
      // Zone filter — intersect with the zone's linked ad IDs
      if (zoneFilteredIds && !zoneFilteredIds.has(id)) return false
      // Type filter (heuristic: large 16:9-ish = video, else banner)
      if (typeFilter) {
        const w = Number(adField(a, 'width', 'Width') || 0)
        const h = Number(adField(a, 'height', 'Height') || 0)
        const looksVideo = w >= 640 && h >= 360 && (w / h).toFixed(2) >= 1.30
        const isVideo = looksVideo
        if (typeFilter === 'video' && !isVideo) return false
        if (typeFilter === 'banner' && isVideo) return false
      }
      return true
    })
  }, [ads, search, tagFilteredIds, zoneFilteredIds, typeFilter])

  // Sort whichever column the user picked.
  const sorted = useMemo(() => {
    const arr = [...filtered]
    arr.sort((a, b) => {
      let av, bv
      if (sortBy === 'size') {
        const wa = Number(adField(a, 'width', 'Width') || 0)
        const ha = Number(adField(a, 'height', 'Height') || 0)
        const wb = Number(adField(b, 'width', 'Width') || 0)
        const hb = Number(adField(b, 'height', 'Height') || 0)
        av = wa * ha
        bv = wb * hb
      } else if (sortBy === 'id' || sortBy === 'weight') {
        av = Number(a[sortBy] ?? a[sortBy.charAt(0).toUpperCase() + sortBy.slice(1)] ?? 0)
        bv = Number(b[sortBy] ?? b[sortBy.charAt(0).toUpperCase() + sortBy.slice(1)] ?? 0)
      } else {
        av = String(a[sortBy] ?? a[sortBy.charAt(0).toUpperCase() + sortBy.slice(1)] ?? '').toLowerCase()
        bv = String(b[sortBy] ?? b[sortBy.charAt(0).toUpperCase() + sortBy.slice(1)] ?? '').toLowerCase()
      }
      const cmp = av < bv ? -1 : av > bv ? 1 : 0
      return sortDir === 'asc' ? cmp : -cmp
    })
    return arr
  }, [filtered, sortBy, sortDir])

  return (
    <div className="sponsors-page">
      <div className="sponsors-header">
        <div className="sponsors-header-left">
          <h1>Ads</h1>
          <span className="sponsors-count">{filtered.length} of {total}</span>
          {stale && (
            <span className="sponsors-stale-badge">
              <AlertTriangle size={11} /> Stale cache
            </span>
          )}
        </div>
        <div className="sponsors-header-right">
          <button
            className="sponsors-refresh-btn"
            onClick={handleRefresh}
            disabled={refreshing || loading}
          >
            <RefreshCw size={14} /> Refresh
          </button>
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
            <Plus size={14} /> New ad
          </button>
        </div>
      </div>

      {status && (
        <div className={`sponsors-status ${status.startsWith('Error') ? 'error' : 'success'}`}>
          <span>{status}</span>
          <button onClick={() => setStatus('')}><X size={14} /></button>
        </div>
      )}

      <div className="adtag-format-row" style={{ marginBottom: 10 }}>
        {STATUS_TABS.map(t => (
          <button
            key={t.value}
            className={`adtag-format-btn ${statusFilter === t.value ? 'active' : ''}`}
            onClick={() => setStatusFilter(t.value)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="zones-toolbar">
        <input
          className="zones-search"
          placeholder="Filter by name or id…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          value={tagFilter}
          onChange={e => setTagFilter(e.target.value)}
          className="zones-search"
          style={{ flex: '0 0 180px' }}
        >
          <option value="">All tags</option>
          {allTbtvTags.map(t => <option key={t} value={t}>tag: {t}</option>)}
        </select>
        <select
          value={zoneFilter}
          onChange={e => setZoneFilter(e.target.value)}
          className="zones-search"
          style={{ flex: '0 0 220px' }}
        >
          <option value="">All zones</option>
          {allZones.map(z => {
            const zid = String(z.id ?? z.ZoneId ?? z.zone_id)
            const zname = z.name ?? z.Name ?? `Zone ${zid}`
            return <option key={zid} value={zid}>in zone: {zname}</option>
          })}
        </select>
        <select
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
          className="zones-search"
          style={{ flex: '0 0 140px' }}
        >
          <option value="">Any type</option>
          <option value="video">Video</option>
          <option value="banner">Banner</option>
        </select>
      </div>

      {selected.size > 0 && (
        <div style={{
          display: 'flex', gap: 10, alignItems: 'center',
          padding: '10px 14px', background: '#1e3a8a30', color: '#93c5fd',
          borderRadius: 6, marginBottom: 10, flexWrap: 'wrap',
        }}>
          <strong>{selected.size} selected</strong>
          <select
            disabled={bulkRunning}
            value=""
            onChange={e => handleBulkStatus(e.target.value)}
            className="zones-search"
            style={{ flex: '0 0 200px' }}
          >
            {BULK_STATUS_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <select
            disabled={bulkRunning}
            value=""
            onChange={e => { handleBulkLinkZone(e.target.value); e.target.value = '' }}
            className="zones-search"
            style={{ flex: '0 0 220px' }}
          >
            <option value="">Link to zone…</option>
            {allZones.map(z => {
              const zid = String(z.id ?? z.ZoneId ?? z.zone_id)
              const zname = z.name ?? z.Name ?? `Zone ${zid}`
              return <option key={zid} value={zid}>→ {zname}</option>
            })}
          </select>
          <select
            disabled={bulkRunning}
            value=""
            onChange={e => { handleBulkLinkGroup(e.target.value); e.target.value = '' }}
            className="zones-search"
            style={{ flex: '0 0 220px' }}
          >
            <option value="">Link to group…</option>
            {allGroups.map(g => {
              const gid = String(g.id ?? g.GroupId)
              const gname = g.name ?? g.Name ?? `Group ${gid}`
              return <option key={gid} value={gid}>→ {gname}</option>
            })}
          </select>
          <select
            disabled={bulkRunning}
            value=""
            onChange={e => { handleBulkLinkAdvertiser(e.target.value); e.target.value = '' }}
            className="zones-search"
            style={{ flex: '0 0 240px' }}
          >
            <option value="">Link to advertiser…</option>
            {[...allAdvertisers]
              .map(a => {
                const aid = String(a.id ?? a.AdvertiserId)
                const username = a.username ?? a.Username ?? ''
                const company = a.cached_company || ''
                // Company name (from the local info cache) is the
                // human-meaningful identifier; fall through to username
                // for advertisers whose detail page hasn't been visited
                // yet (cache empty).
                const label = company
                  ? (username ? `${company} (${username})` : company)
                  : (username || `Advertiser ${aid}`)
                const sortKey = (company || username || aid).toLowerCase()
                return { aid, label, sortKey }
              })
              .sort((x, y) => x.sortKey.localeCompare(y.sortKey))
              .map(({ aid, label }) => (
                <option key={aid} value={aid}>→ {label}</option>
              ))}
          </select>
          <button
            className="sponsors-refresh-btn"
            onClick={() => setShowBulkTags(true)}
            disabled={bulkRunning}
          >
            <Tag size={13} /> Apply tags
          </button>
          <button
            className="sponsors-refresh-btn"
            onClick={() => setSelected(new Set())}
            disabled={bulkRunning}
            style={{ marginLeft: 'auto' }}
          >
            <X size={13} /> Clear
          </button>
          {bulkRunning && <span className="muted" style={{ fontSize: '0.8rem' }}>Working…</span>}
        </div>
      )}

      {loading ? (
        <div className="muted">Loading ads…</div>
      ) : filtered.length === 0 ? (
        <div className="muted">No ads found.</div>
      ) : (
        <table className="zones-table">
          <thead>
            <tr>
              <th style={{ width: 32 }}>
                <input
                  type="checkbox"
                  checked={sorted.length > 0 && sorted.every(a => selected.has(String(adField(a, 'id', 'AdId'))))}
                  onChange={toggleSelectAll}
                  title="Select all visible"
                />
              </th>
              {[
                { key: 'id',     label: 'ID' },
                { key: 'name',   label: 'Name' },
                { key: 'status', label: 'Status' },
                { key: 'weight', label: 'Weight' },
                { key: 'size',   label: 'Size' },
              ].map(c => (
                <th
                  key={c.key}
                  onClick={() => handleSort(c.key)}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  title="Click to sort"
                >
                  {c.label}
                  {sortBy === c.key && <span style={{ marginLeft: 4, color: '#f59e0b' }}>
                    {sortDir === 'asc' ? '↑' : '↓'}
                  </span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map(a => {
              const id = String(adField(a, 'id', 'AdId'))
              const w = adField(a, 'width', 'Width')
              const h = adField(a, 'height', 'Height')
              const isSel = selected.has(id)
              return (
                <tr
                  key={id}
                  onClick={() => navigate(`/sponsors/ads/${id}`)}
                  style={isSel ? { background: '#1e3a8a30' } : undefined}
                >
                  <td onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={isSel} onChange={() => toggleSelect(id)} />
                  </td>
                  <td>{id}</td>
                  <td>{adField(a, 'name', 'Name')}</td>
                  <td>{adField(a, 'status', 'Status')}</td>
                  <td>{adField(a, 'weight', 'Weight')}</td>
                  <td>{w && h ? `${w}x${h}` : ''}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}

      {showCreate && (
        <AdEditor
          onClose={() => setShowCreate(false)}
          onSubmit={handleCreate}
        />
      )}

      {showBulkTags && (
        <BulkTagsModal
          count={selected.size}
          entityLabel="ad"
          onClose={() => setShowBulkTags(false)}
          onSubmit={handleBulkTags}
        />
      )}
    </div>
  )
}
